/*
 * fk-waitlist.js — Back-in-stock waitlist modal for sold-out product cards.
 *
 * SIGNUP PATH (verified choice):
 *   Klaviyo Client API — Create Back In Stock Subscription.
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=<public id>
 *   Headers: Content-Type: application/json, revision: 2024-10-15
 *   Body (JSON:API):
 *     data.type                         = "back-in-stock-subscription"
 *     data.attributes.profile.data      = { type: "profile", attributes: { email } }
 *     data.attributes.channels          = ["EMAIL"]
 *     data.relationships.variant.data   = { type: "catalog-variant",
 *                                           id: "$shopify:::$default:::<shopify variant id>" }
 *   Success = HTTP 202 Accepted.
 *
 * The older onsite endpoint (a.klaviyo.com/onsite/components/back-in-stock/subscribe)
 * was tested against this store and now returns HTTP 410 "This API has been
 * retired", so it is NOT used.
 *
 * Company id and all copy come from window.FKWaitlistConfig, printed by
 * snippets/fk-waitlist.liquid. The public company id (Vv2sAF) was read from the
 * storefront's klaviyo.js tag:
 *   static.klaviyo.com/onsite/js/klaviyo.js?company_id=Vv2sAF
 *
 * Styles and modal DOM are created here (once) rather than in Liquid, because
 * card-product.liquid renders per card and {% render %} scopes cannot share an
 * "already printed" flag — building in JS guarantees a single element id.
 *
 * Every failure path (bad email, HTTP error, network error, 15s timeout,
 * missing company id) surfaces the inline error state. Success is only shown
 * on a 2xx from Klaviyo — the modal never fakes it.
 */
(function () {
  'use strict';

  if (window.__fkWaitlistInit) return;
  window.__fkWaitlistInit = true;

  var API_BASE = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var API_REVISION = '2024-10-15';
  var VARIANT_PREFIX = '$shopify:::$default:::';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var TIMEOUT_MS = 15000;

  var DEFAULTS = {
    company: '',
    heading: 'Get notified',
    body: "Drop your email and we'll let you know the moment it's back in stock.",
    submit: 'Notify me',
    submitBusy: 'Signing you up…',
    successHeading: "You're in",
    success: "You're on the list — we'll email you when it's back.",
    done: 'Keep shopping',
    close: 'Close',
    emailLabel: 'Email address',
    emailPlaceholder: 'you@email.com',
    errorInvalid: 'Enter a valid email address.',
    errorGeneric: 'Something went wrong — please try again.',
    errorTimeout: 'That took too long. Check your connection and try again.',
    errorConfig: 'We could not set up the alert for this item. Please try again later.'
  };

  var cfg = {};
  var supplied = window.FKWaitlistConfig || {};
  Object.keys(DEFAULTS).forEach(function (k) {
    cfg[k] = typeof supplied[k] === 'string' && supplied[k].length ? supplied[k] : DEFAULTS[k];
  });

  var CSS = [
    '.fk-waitlist-modal{position:fixed;inset:0;z-index:100000;display:flex;align-items:center;',
    'justify-content:center;padding:16px}',
    '.fk-waitlist-modal[hidden]{display:none!important}',
    '.fk-waitlist-modal__backdrop{position:absolute;inset:0;background:rgba(0,0,0,.6);cursor:pointer}',
    '.fk-waitlist-modal__dialog{position:relative;width:100%;max-width:420px;background:#fff;',
    'color:#0a0a0a;border:1px solid #0a0a0a;padding:28px 24px 24px;box-shadow:0 18px 50px rgba(0,0,0,.25)}',
    '.fk-waitlist-modal__close{position:absolute;top:4px;right:4px;width:44px;height:44px;display:flex;',
    'align-items:center;justify-content:center;background:transparent;border:0;color:#0a0a0a;',
    'font-size:2.4rem;line-height:1;cursor:pointer}',
    '.fk-waitlist-modal__close:focus-visible{outline:2px solid #0a0a0a;outline-offset:-2px}',
    '.fk-waitlist-modal__heading{margin:0 0 6px;font-family:var(--font-heading-family,inherit);',
    'font-size:2.2rem;line-height:1.1;letter-spacing:.04em;text-transform:uppercase}',
    '.fk-waitlist-modal__product{display:block;margin:0 0 10px;font-size:1.3rem;letter-spacing:.08em;',
    'text-transform:uppercase;opacity:.65}',
    '.fk-waitlist-modal__product:empty{display:none}',
    '.fk-waitlist-modal__text{margin:0 0 16px;font-size:1.4rem;line-height:1.45}',
    '.fk-waitlist-modal__input{width:100%;min-height:46px;padding:12px 14px;margin:0 0 12px;',
    'border:1px solid #0a0a0a;border-radius:0;background:#fff;color:#0a0a0a;font-size:1.6rem}',
    '.fk-waitlist-modal__input[aria-invalid="true"]{border-color:#b3261e}',
    '.fk-waitlist-modal__submit{width:100%;min-height:46px;padding:12px 16px;border:1px solid #0a0a0a;',
    'border-radius:0;background:#0a0a0a;color:#fff;font-family:var(--font-heading-family,inherit);',
    'font-size:1.4rem;letter-spacing:.12em;text-transform:uppercase;cursor:pointer}',
    '.fk-waitlist-modal__submit[disabled]{opacity:.6;cursor:default}',
    '.fk-waitlist-modal__error{margin:0 0 12px;color:#b3261e;font-size:1.3rem;line-height:1.4}',
    '.fk-waitlist-modal__error[hidden],.fk-waitlist-modal__success[hidden],',
    '.fk-waitlist-modal__panel[hidden]{display:none}',
    '.fk-waitlist-modal__success p{margin:0 0 18px;font-size:1.5rem;line-height:1.45}',
    '.fk-waitlist-modal__success:focus{outline:none}',
    'html.fk-waitlist-open,html.fk-waitlist-open body{overflow:hidden}'
  ].join('');

  var modal = null;
  var els = {};
  var lastOpener = null;
  var current = { variant: '', product: '' };

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function injectStyles() {
    if (document.getElementById('FkWaitlistStyles')) return;
    var style = document.createElement('style');
    style.id = 'FkWaitlistStyles';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  function build() {
    if (modal) return modal;
    injectStyles();

    modal = document.createElement('div');
    modal.id = 'FkWaitlistModal';
    modal.className = 'fk-waitlist-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'FkWaitlistHeading');
    modal.hidden = true;
    modal.innerHTML = [
      '<div class="fk-waitlist-modal__backdrop" data-fk-backdrop></div>',
      '<div class="fk-waitlist-modal__dialog" data-fk-dialog>',
      '<button type="button" class="fk-waitlist-modal__close" data-fk-close aria-label="',
      esc(cfg.close),
      '">&times;</button>',
      '<div class="fk-waitlist-modal__panel" data-fk-panel>',
      '<h2 class="fk-waitlist-modal__heading" id="FkWaitlistHeading">',
      esc(cfg.heading),
      '</h2>',
      '<span class="fk-waitlist-modal__product" data-fk-product></span>',
      '<p class="fk-waitlist-modal__text">',
      esc(cfg.body),
      '</p>',
      '<form data-fk-form novalidate>',
      '<label class="visually-hidden" for="FkWaitlistEmail">',
      esc(cfg.emailLabel),
      '</label>',
      '<input type="email" id="FkWaitlistEmail" name="email" class="fk-waitlist-modal__input" ',
      'data-fk-email autocomplete="email" inputmode="email" placeholder="',
      esc(cfg.emailPlaceholder),
      '" required>',
      '<p class="fk-waitlist-modal__error" data-fk-error role="alert" hidden></p>',
      '<button type="submit" class="fk-waitlist-modal__submit" data-fk-submit>',
      esc(cfg.submit),
      '</button>',
      '</form>',
      '</div>',
      '<div class="fk-waitlist-modal__success" data-fk-success tabindex="-1" role="status" hidden>',
      '<h2 class="fk-waitlist-modal__heading">',
      esc(cfg.successHeading),
      '</h2>',
      '<p>',
      esc(cfg.success),
      '</p>',
      '<button type="button" class="fk-waitlist-modal__submit" data-fk-done>',
      esc(cfg.done),
      '</button>',
      '</div>',
      '</div>'
    ].join('');

    document.body.appendChild(modal);

    els.dialog = modal.querySelector('[data-fk-dialog]');
    els.panel = modal.querySelector('[data-fk-panel]');
    els.form = modal.querySelector('[data-fk-form]');
    els.email = modal.querySelector('[data-fk-email]');
    els.submit = modal.querySelector('[data-fk-submit]');
    els.product = modal.querySelector('[data-fk-product]');
    els.error = modal.querySelector('[data-fk-error]');
    els.success = modal.querySelector('[data-fk-success]');

    modal.querySelector('[data-fk-backdrop]').addEventListener('click', close);
    modal.querySelector('[data-fk-close]').addEventListener('click', close);
    modal.querySelector('[data-fk-done]').addEventListener('click', close);
    els.form.addEventListener('submit', onSubmit);

    return modal;
  }

  function setError(msg) {
    if (msg) {
      els.error.textContent = msg;
      els.error.hidden = false;
      els.email.setAttribute('aria-invalid', 'true');
    } else {
      els.error.textContent = '';
      els.error.hidden = true;
      els.email.removeAttribute('aria-invalid');
    }
  }

  function setBusy(busy) {
    els.submit.disabled = !!busy;
    els.submit.setAttribute('aria-busy', busy ? 'true' : 'false');
    els.submit.textContent = busy ? cfg.submitBusy : cfg.submit;
  }

  function reset() {
    setError('');
    setBusy(false);
    els.panel.hidden = false;
    els.success.hidden = true;
    els.form.reset();
  }

  function open(opener) {
    build();
    lastOpener = opener || null;
    current.variant = opener ? opener.getAttribute('data-fk-variant') || '' : '';
    current.product = opener ? opener.getAttribute('data-fk-product') || '' : '';
    reset();
    els.product.textContent = current.product;
    modal.hidden = false;
    document.documentElement.classList.add('fk-waitlist-open');
    window.requestAnimationFrame(function () {
      els.email.focus();
    });
    document.addEventListener('keydown', onKeydown, true);
  }

  function close() {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.documentElement.classList.remove('fk-waitlist-open');
    document.removeEventListener('keydown', onKeydown, true);
    if (lastOpener && document.contains(lastOpener)) {
      try {
        lastOpener.focus();
      } catch (e) {
        /* no-op */
      }
    }
    lastOpener = null;
  }

  function onKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    var focusables = els.dialog.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function subscribe(email) {
    if (!cfg.company || !current.variant) {
      return Promise.reject(new Error('config'));
    }

    var payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email }
            }
          },
          channels: ['EMAIL']
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: VARIANT_PREFIX + current.variant
            }
          }
        }
      }
    };

    return fetch(API_BASE + '?company_id=' + encodeURIComponent(cfg.company), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: API_REVISION
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (response.ok) return true;
      throw new Error('http_' + response.status);
    });
  }

  function onSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    var email = ((els.email && els.email.value) || '').trim();
    if (!EMAIL_RE.test(email)) {
      setError(cfg.errorInvalid);
      els.email.focus();
      return;
    }

    setError('');
    setBusy(true);

    var settled = false;
    var timer = window.setTimeout(function () {
      if (settled) return;
      settled = true;
      setBusy(false);
      setError(cfg.errorTimeout);
    }, TIMEOUT_MS);

    subscribe(email)
      .then(function () {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        setBusy(false);
        els.panel.hidden = true;
        els.success.hidden = false;
        els.success.focus();
      })
      .catch(function (err) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        setBusy(false);
        setError(err && err.message === 'config' ? cfg.errorConfig : cfg.errorGeneric);
      });
  }

  // Capture-phase delegation so cards added later (filters, pagination,
  // section rendering) work, and so the card's wrapping link never wins.
  document.addEventListener(
    'click',
    function (event) {
      var trigger = event.target.closest && event.target.closest('[data-fk-waitlist-open]');
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      open(trigger);
    },
    true
  );
})();